import React, { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { useSuspendPollingWhileOpen } from "@/lib/dialogPolling";
import { toast } from "sonner";
import { X, Plus, Check, Pencil, Trash2, RotateCcw, GripVertical } from "lucide-react";

const CADENCES = [
  { key: "daily",  label: "Every day" },
  { key: "weekly", label: "Specific days" },
  { key: "once",   label: "One-off" },
];
const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];
const WEEKDAY_FULL = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const emptyDraft = {
  title: "",
  assignee_id: "",
  cadence: "daily",
  weekdays: [],
  due_date: "",
  due_time: "",
  push_enabled: false,
};

function describeCadence(c) {
  const time = c.due_time ? ` · ${c.due_time}` : "";
  if (c.cadence === "daily") return `Daily${time}`;
  if (c.cadence === "once") return (c.due_date ? `Once · ${c.due_date}` : "One-off") + time;
  if (c.cadence === "weekly") {
    if (!c.weekdays?.length) return `Weekly${time}`;
    return c.weekdays.map((d) => WEEKDAY_FULL[d]).join(" · ") + time;
  }
  return c.cadence + time;
}

/**
 * ChoresSheet — every-tier task list. Parents and kids can create chores;
 * anyone assigned (plus any parent) can mark them done today. Cadences:
 * one-off, daily, weekly (weekday picker).
 *
 * Props:
 *   members  — family members (pets auto-filtered)
 *   viewerId — current viewer (used for permission checks + default assignee)
 *   onClose  — close handler
 */
export default function ChoresSheet({ members, viewerId, onClose }) {
  useSuspendPollingWhileOpen();

  const nonPets = useMemo(
    () => (members || []).filter((m) => m.role !== "pet"),
    [members]
  );
  const viewer = members.find((m) => m.id === viewerId);
  const isParent = !viewer || viewer.role === "parent";

  const [chores, setChores] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);

  const load = useCallback(async () => {
    try {
      const rows = await api.listChores();
      setChores(rows);
    } catch {
      toast.error("Could not load chores");
    }
  }, []);

  useEffect(() => {
    load();
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [load, onClose]);

  const openAdd = () => {
    setEditingId(null);
    setDraft({
      ...emptyDraft,
      assignee_id: nonPets[0]?.id || "",
    });
    setAddOpen(true);
  };

  // Drag-to-reorder shared state. We track which chore is being dragged
  // so we can reorder optimistically on drop, then PATCH the new order
  // to the backend. The drop target's chore id determines insertion
  // point — drop X onto Y → X lands immediately before Y.
  const dragRef = React.useRef(null);
  const reorderChores = async (draggedId, targetId) => {
    if (!draggedId || draggedId === targetId) return;
    setChores((prev) => {
      const ids = prev.map((c) => c.id);
      const fromIdx = ids.indexOf(draggedId);
      if (fromIdx === -1) return prev;
      const next = prev.slice();
      const [moved] = next.splice(fromIdx, 1);
      const toIdx = targetId
        ? next.findIndex((c) => c.id === targetId)
        : next.length;
      next.splice(toIdx === -1 ? next.length : toIdx, 0, moved);
      // Fire-and-forget the persist; UI already shows the new order.
      api
        .reorderChores(next.map((c) => c.id))
        .catch(() => {
          toast.error("Couldn't save order");
          load();
        });
      return next;
    });
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setDraft({
      title: c.title,
      assignee_id: c.assignee_id,
      cadence: c.cadence,
      weekdays: c.weekdays || [],
      due_date: c.due_date || "",
      due_time: c.due_time || "",
      push_enabled: Boolean(c.push_enabled),
    });
    setAddOpen(true);
  };

  const save = async () => {
    if (!draft.title.trim()) return toast.error("Title required");
    if (!draft.assignee_id) return toast.error("Pick who it's for");
    if (draft.cadence === "weekly" && draft.weekdays.length === 0) {
      return toast.error("Pick at least one weekday");
    }
    // due_time validation — empty string is allowed (= all day). When
    // set, must match HH:MM. The native <input type="time"> already
    // enforces this, but a paste of "9:5" would slip through.
    if (draft.due_time && !/^\d{2}:\d{2}$/.test(draft.due_time)) {
      return toast.error("Time must be HH:MM (24h)");
    }
    setBusy(true);
    try {
      const payload = {
        title: draft.title.trim(),
        assignee_id: draft.assignee_id,
        cadence: draft.cadence,
        weekdays: draft.cadence === "weekly" ? draft.weekdays : [],
        due_date: draft.cadence === "once" ? (draft.due_date || "") : "",
        due_time: draft.due_time || "",
        // If the user picked "All day" we always send push_enabled=false
        // so the backend can confidently turn the reminder off. With a
        // time set, send whatever they ticked.
        push_enabled: draft.due_time ? Boolean(draft.push_enabled) : false,
      };
      if (editingId) {
        await api.updateChore(editingId, payload);
      } else {
        await api.createChore(payload);
      }
      setAddOpen(false);
      setEditingId(null);
      setDraft(emptyDraft);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete “${c.title}”?`)) return;
    try {
      await api.deleteChore(c.id);
      setChores((prev) => prev.filter((x) => x.id !== c.id));
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const toggleDone = async (c) => {
    try {
      const updated = await api.completeChore(c.id, {
        member_id: viewerId || c.assignee_id,
        done: !c.completed_today,
      });
      setChores((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't update";
      if (e?.response?.status === 403) {
        toast.info(msg);
      } else {
        toast.error(msg);
      }
    }
  };

  // Split into today's list + archive (not due today or inactive)
  const todayList = (chores || []).filter((c) => c.due_today);
  const laterList = (chores || []).filter((c) => !c.due_today);

  return (
    <div
      className="fixed inset-0 z-50 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-stretch md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="chores-sheet"
    >
      <div
        className="relative bg-[color:var(--bg)] md:rounded-[20px] w-full md:max-w-2xl max-h-[100vh] md:max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[color:var(--bg)] px-5 md:px-7 pt-5 pb-3 flex items-center justify-between border-b border-[color:var(--line-soft)] z-10">
          <div>
            <div className="label-tiny">Today</div>
            <h2 className="font-display-italic text-3xl md:text-4xl leading-none mt-1 tracking-tight">
              Chores
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openAdd}
              className="ff-btn ff-btn-primary inline-flex items-center gap-1"
              data-testid="chores-add-btn"
            >
              <Plus className="w-4 h-4" strokeWidth={2} /> Add
            </button>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
              aria-label="Close"
              data-testid="chores-close"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="px-5 md:px-7 py-5 space-y-8">
          {chores === null ? (
            <div className="text-[color:var(--ink-3)] text-sm">Loading…</div>
          ) : (
            <>
              <section>
                <div className="label-tiny mb-3">
                  Due today ({todayList.filter((c) => c.completed_today).length}/
                  {todayList.length})
                </div>
                {todayList.length === 0 ? (
                  <div className="text-[14px] text-[color:var(--ink-3)]">
                    Nothing due today. {isParent ? "Tap Add to create the first one." : ""}
                  </div>
                ) : (
                  <ul className="space-y-2" data-testid="chores-today-list">
                    {todayList.map((c) => {
                      const assignee = members.find((m) => m.id === c.assignee_id);
                      const completedBy = c.completed_by
                        ? members.find((m) => m.id === c.completed_by)?.name
                        : null;
                      return (
                        <li
                          key={c.id}
                          className={`ff-card p-3 flex items-center gap-3 ${c.completed_today ? "opacity-60" : ""}`}
                          data-testid={`chore-row-${c.id}`}
                          draggable
                          onDragStart={(e) => {
                            dragRef.current = c.id;
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", c.id);
                          }}
                          onDragOver={(e) => {
                            if (dragRef.current && dragRef.current !== c.id) {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = "move";
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const id = dragRef.current || e.dataTransfer.getData("text/plain");
                            dragRef.current = null;
                            reorderChores(id, c.id);
                          }}
                        >
                          <span
                            className="hidden md:inline-flex shrink-0 text-[color:var(--ink-3)] cursor-grab active:cursor-grabbing"
                            aria-hidden="true"
                            data-testid={`chore-drag-${c.id}`}
                          >
                            <GripVertical className="w-4 h-4" strokeWidth={2} />
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleDone(c)}
                            className={`w-8 h-8 shrink-0 rounded-full border flex items-center justify-center transition-colors ${
                              c.completed_today
                                ? "bg-[#CDE6D2] border-[#7db389]"
                                : "border-[color:var(--line)] hover:bg-[color:var(--bg-soft)]"
                            }`}
                            aria-label={c.completed_today ? "Mark not done" : "Mark done"}
                            data-testid={`chore-toggle-${c.id}`}
                          >
                            {c.completed_today ? (
                              <Check className="w-4 h-4" strokeWidth={2.5} />
                            ) : null}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div
                              className={`text-[15px] leading-tight ${
                                c.completed_today
                                  ? "line-through text-[color:var(--ink-2)]"
                                  : "text-[color:var(--ink)]"
                              }`}
                            >
                              {c.title}
                              {c.overdue_today && !c.completed_today && (
                                <span
                                  className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] font-semibold text-[color:var(--rough-strong)]"
                                  data-testid={`chore-overdue-${c.id}`}
                                >
                                  · Overdue
                                </span>
                              )}
                            </div>
                            <div className="text-[12px] text-[color:var(--ink-3)] mt-0.5 inline-flex items-center gap-1 flex-wrap">
                              {assignee?.avatar_image ? (
                                <img
                                  src={assignee.avatar_image}
                                  alt=""
                                  className="w-4 h-4 rounded-full object-cover inline-block"
                                />
                              ) : (
                                <span className="text-base leading-none">
                                  {assignee?.avatar_emoji || "🙂"}
                                </span>
                              )}
                              <span>
                                {assignee?.name || "—"} · {describeCadence(c)}
                                {c.completed_today && completedBy && completedBy !== assignee?.name
                                  ? ` · by ${completedBy}`
                                  : ""}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => openEdit(c)}
                              className="p-1.5 rounded-full hover:bg-[color:var(--bg-soft)]"
                              aria-label="Edit"
                              data-testid={`chore-edit-${c.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                            </button>
                            {isParent && (
                              <button
                                type="button"
                                onClick={() => remove(c)}
                                className="p-1.5 rounded-full hover:bg-[color:var(--bg-soft)] text-[color:var(--ink-3)]"
                                aria-label="Delete"
                                data-testid={`chore-delete-${c.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {laterList.length > 0 && (
                <section>
                  <div className="label-tiny mb-3">Not due today</div>
                  <ul className="space-y-2">
                    {laterList.map((c) => {
                      const assignee = members.find((m) => m.id === c.assignee_id);
                      return (
                        <li
                          key={c.id}
                          className="ff-card p-3 flex items-center gap-3"
                          data-testid={`chore-row-${c.id}`}
                          draggable
                          onDragStart={(e) => {
                            dragRef.current = c.id;
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", c.id);
                          }}
                          onDragOver={(e) => {
                            if (dragRef.current && dragRef.current !== c.id) {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = "move";
                            }
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const id = dragRef.current || e.dataTransfer.getData("text/plain");
                            dragRef.current = null;
                            reorderChores(id, c.id);
                          }}
                        >
                          <span
                            className="hidden md:inline-flex shrink-0 text-[color:var(--ink-3)] cursor-grab active:cursor-grabbing"
                            aria-hidden="true"
                            data-testid={`chore-drag-${c.id}`}
                          >
                            <GripVertical className="w-4 h-4" strokeWidth={2} />
                          </span>
                          <RotateCcw className="w-4 h-4 text-[color:var(--ink-3)] shrink-0" strokeWidth={1.75} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[15px] leading-tight text-[color:var(--ink)]">
                              {c.title}
                            </div>
                            <div className="text-[12px] text-[color:var(--ink-3)] mt-0.5 inline-flex items-center gap-1 flex-wrap">
                              {assignee?.avatar_image ? (
                                <img
                                  src={assignee.avatar_image}
                                  alt=""
                                  className="w-4 h-4 rounded-full object-cover inline-block"
                                />
                              ) : (
                                <span className="text-base leading-none">
                                  {assignee?.avatar_emoji || "🙂"}
                                </span>
                              )}
                              <span>
                                {assignee?.name || "—"} · {describeCadence(c)}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => openEdit(c)}
                              className="p-1.5 rounded-full hover:bg-[color:var(--bg-soft)]"
                              aria-label="Edit"
                              data-testid={`chore-edit-${c.id}`}
                            >
                              <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} />
                            </button>
                            {isParent && (
                              <button
                                type="button"
                                onClick={() => remove(c)}
                                className="p-1.5 rounded-full hover:bg-[color:var(--bg-soft)] text-[color:var(--ink-3)]"
                                aria-label="Delete"
                                data-testid={`chore-delete-${c.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>

        {/* Add / Edit modal */}
        {addOpen && (
          <div
            className="fixed inset-0 z-60 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => {
              setAddOpen(false);
              setEditingId(null);
            }}
            data-testid="chore-form"
          >
            <div
              className="bg-[color:var(--bg)] rounded-[18px] w-full max-w-md p-6 space-y-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-display-italic text-2xl tracking-tight">
                  {editingId ? "Edit chore" : "New chore"}
                </h3>
                <button
                  onClick={() => {
                    setAddOpen(false);
                    setEditingId(null);
                  }}
                  className="w-8 h-8 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
                  aria-label="Close"
                  data-testid="chore-form-close"
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>

              <div>
                <label className="label-tiny block mb-2">Chore</label>
                <input
                  autoFocus
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder="e.g. Empty dishwasher"
                  maxLength={80}
                  className="ff-input"
                  data-testid="chore-title-input"
                />
              </div>

              <div>
                <label className="label-tiny block mb-2">For</label>
                <div className="flex flex-wrap gap-1.5" data-testid="chore-assignee-row">
                  {nonPets.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => setDraft({ ...draft, assignee_id: m.id })}
                      className={`ff-chip inline-flex items-center gap-1 ${draft.assignee_id === m.id ? "is-active" : ""}`}
                      data-testid={`chore-assignee-${m.id}`}
                    >
                      {m.avatar_image ? (
                        <img
                          src={m.avatar_image}
                          alt=""
                          className="w-4 h-4 rounded-full object-cover"
                        />
                      ) : (
                        <span className="text-base leading-none">{m.avatar_emoji}</span>
                      )}
                      <span>{m.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="label-tiny block mb-2">Repeats</label>
                <div className="flex flex-wrap gap-1.5">
                  {CADENCES.map((c) => (
                    <button
                      key={c.key}
                      onClick={() => setDraft({ ...draft, cadence: c.key })}
                      className={`ff-chip ${draft.cadence === c.key ? "is-active" : ""}`}
                      data-testid={`chore-cadence-${c.key}`}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                {draft.cadence === "weekly" && (
                  <div className="mt-3">
                    <div className="label-tiny mb-1">Which days?</div>
                    <div className="flex gap-1" data-testid="chore-weekday-row">
                      {WEEKDAY_LABELS.map((lbl, i) => {
                        const on = draft.weekdays.includes(i);
                        return (
                          <button
                            key={i}
                            onClick={() =>
                              setDraft({
                                ...draft,
                                weekdays: on
                                  ? draft.weekdays.filter((x) => x !== i)
                                  : [...draft.weekdays, i].sort(),
                              })
                            }
                            className={`w-9 h-9 rounded-full border text-[13px] font-medium transition-colors ${
                              on
                                ? "bg-[color:var(--ink)] text-[color:var(--bg)] border-[color:var(--ink)]"
                                : "border-[color:var(--line)] hover:bg-[color:var(--bg-soft)]"
                            }`}
                            data-testid={`chore-weekday-${i}`}
                            aria-label={WEEKDAY_FULL[i]}
                          >
                            {lbl}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {draft.cadence === "once" && (
                  <div className="mt-3">
                    <label className="label-tiny block mb-1">Due date (optional)</label>
                    <input
                      type="date"
                      value={draft.due_date}
                      onChange={(e) => setDraft({ ...draft, due_date: e.target.value })}
                      className="ff-input max-w-[200px]"
                      data-testid="chore-due-date"
                    />
                  </div>
                )}

                {/* Time row — independent of cadence. "All day" pill
                    keeps it out of the way for chores that don't need
                    a specific moment (most of them). Choosing a time
                    reveals the "Push at this time" toggle, default
                    on (mirrors the backend's auto-default). */}
                <div className="mt-3" data-testid="chore-time-row">
                  <label className="label-tiny block mb-2">Time</label>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() =>
                        setDraft({ ...draft, due_time: "", push_enabled: false })
                      }
                      className={`ff-chip ${!draft.due_time ? "is-active" : ""}`}
                      data-testid="chore-time-allday"
                    >
                      All day
                    </button>
                    <input
                      type="time"
                      value={draft.due_time}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          due_time: e.target.value,
                          // Auto-flip push ON when the user first picks
                          // a time. They can still untick it below.
                          push_enabled:
                            !draft.due_time && e.target.value
                              ? true
                              : draft.push_enabled,
                        })
                      }
                      className="ff-input py-1.5 text-[13px] w-auto min-w-[120px]"
                      data-testid="chore-due-time"
                      aria-label="Specific time"
                    />
                  </div>
                  {draft.due_time && (
                    <label
                      className="mt-3 flex items-center gap-2 text-[13px] text-[color:var(--ink-2)] cursor-pointer select-none"
                      data-testid="chore-push-row"
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(draft.push_enabled)}
                        onChange={(e) =>
                          setDraft({ ...draft, push_enabled: e.target.checked })
                        }
                        className="w-4 h-4 accent-[color:var(--ink)]"
                        data-testid="chore-push-toggle"
                      />
                      Send a push at {draft.due_time}
                    </label>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => {
                    setAddOpen(false);
                    setEditingId(null);
                  }}
                  className="ff-btn"
                  data-testid="chore-form-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={save}
                  disabled={busy}
                  className="ff-btn ff-btn-primary"
                  data-testid="chore-form-save"
                >
                  {busy ? "Saving…" : editingId ? "Save" : "Add chore"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
